import { CategoryAxis, ValueAxis } from "@/components/tool-kit/charts/axes";
import { ChartSvg, ChartTitles } from "@/components/tool-kit/charts/chart-svg";
import type { ChartStyle } from "@/components/tool-kit/charts/controls";
import { SvgLegend } from "@/components/tool-kit/charts/legend";
import { px } from "@/components/tool-kit/charts/marks";
import {
  type BarMode,
  type BarOrientation,
  type BarRect,
  barExtent,
  layoutBars,
  rowTotal,
  stackRow,
} from "@/lib/tool-kit/charts/bars";
import {
  colorIndex,
  paletteColor,
  textColorOn,
} from "@/lib/tool-kit/charts/color";
import {
  type NumberFormat,
  tickFormat,
  valueText,
} from "@/lib/tool-kit/charts/format";
import {
  type CategoryLabelLayout,
  CHART_FONT_SIZE,
  CHART_HEIGHT,
  CHART_PADDING,
  CHART_WIDTH,
  cartesianFrame,
  type Frame,
  type FrameInput,
  heightForLabels,
  heightForPlot,
  type LegendLayout,
  layoutCategoryLabels,
  layoutLegend,
  rotatedOverflow,
} from "@/lib/tool-kit/charts/layout";
import {
  type BandScale,
  bandScale,
  linearScale,
  niceTicks,
  type Scale,
} from "@/lib/tool-kit/charts/scale";
import {
  estimateTextWidth,
  maxTextWidth,
  wrapText,
} from "@/lib/tool-kit/charts/text";
import { CHART_INK } from "@/lib/tool-kit/palette";

/*
 * A bar chart: vertical columns or horizontal bars, one series or several (grouped side
 * by side or stacked). The value axis always starts at zero — a bar's length IS its
 * value, so a truncated axis misstates every bar on it.
 *
 * Horizontal charts grow in height with their categories, so twenty long labels stay
 * readable; vertical charts keep a fixed height and wrap or rotate their labels.
 */

export interface BarCategory {
  label: string;
  /** One value per series; null draws nothing. */
  values: (number | null)[];
}

type FrameBase = Omit<
  FrameInput,
  "height" | "leftLabelWidth" | "bottomLabelHeight"
>;

const BAND_PADDING = { paddingInner: 0.3, paddingOuter: 0.15 };
const TICK_STYLE = { fontSize: CHART_FONT_SIZE.tick };
const VALUE_SIZE = CHART_FONT_SIZE.value;

/** A bar's value: past its end, or — for a stacked segment — inside it when it fits. */
function BarValueLabel({
  bar,
  orientation,
  inside,
  fill,
  text,
}: {
  bar: BarRect;
  orientation: BarOrientation;
  inside: boolean;
  fill: string;
  text: string;
}) {
  if (inside) {
    const width = estimateTextWidth(text, { fontSize: VALUE_SIZE });
    if (bar.width < width + 6 || bar.height < VALUE_SIZE + 4) {
      return null;
    }
    return (
      <text
        fill={textColorOn(fill)}
        fontSize={VALUE_SIZE}
        textAnchor="middle"
        x={px(bar.x + bar.width / 2)}
        y={px(bar.y + bar.height / 2 + VALUE_SIZE * 0.35)}
      >
        {text}
      </text>
    );
  }
  const negative = bar.value < 0;
  if (orientation === "vertical") {
    return (
      <text
        fill={CHART_INK.text}
        fontSize={VALUE_SIZE}
        textAnchor="middle"
        x={px(bar.x + bar.width / 2)}
        y={px(negative ? bar.y + bar.height + VALUE_SIZE : bar.y - 4)}
      >
        {text}
      </text>
    );
  }
  return (
    <text
      fill={CHART_INK.text}
      fontSize={VALUE_SIZE}
      textAnchor={negative ? "end" : "start"}
      x={px(negative ? bar.x - 4 : bar.x + bar.width + 4)}
      y={px(bar.y + bar.height / 2 + VALUE_SIZE * 0.35)}
    >
      {text}
    </text>
  );
}

/** Columns: categories along the foot, a fixed height. */
function verticalFrame(
  base: FrameBase,
  categories: BarCategory[],
  tickLabels: string[],
  valueLabels: boolean
): { frame: Frame; labels: CategoryLabelLayout } {
  const input = {
    ...base,
    height: CHART_HEIGHT,
    leftLabelWidth: maxTextWidth(tickLabels, TICK_STYLE),
    topPadding: valueLabels ? 18 : 8,
  };
  const probe = cartesianFrame({ ...input, bottomLabelHeight: 0 });
  const step = bandScale(
    categories.length,
    [0, probe.plot.width],
    BAND_PADDING
  ).step;
  const labels = layoutCategoryLabels(
    categories.map((c) => c.label),
    step
  );
  const sized = {
    ...input,
    height: heightForLabels(labels.height),
    bottomLabelHeight: labels.height,
  };
  const frame = cartesianFrame(sized);
  const firstCenter =
    step * (BAND_PADDING.paddingOuter + (1 - BAND_PADDING.paddingInner) / 2);
  const overflow = rotatedOverflow(frame, labels, firstCenter);
  return {
    frame:
      overflow > 0
        ? cartesianFrame({
            ...sized,
            leftLabelWidth: sized.leftLabelWidth + overflow,
          })
        : frame,
    labels,
  };
}

/** Bars: categories down the side, a height that grows with them. */
function horizontalFrame(
  base: FrameBase,
  categories: BarCategory[],
  valueLabelWidth: number,
  perRow: number
): { frame: Frame; labels: CategoryLabelLayout } {
  const lines = categories.map((c) =>
    wrapText(c.label, Math.min(200, CHART_WIDTH * 0.32), TICK_STYLE, 2)
  );
  const input = {
    ...base,
    leftLabelWidth: maxTextWidth(lines.flat(), TICK_STYLE),
    bottomLabelHeight: CHART_FONT_SIZE.tick,
    rightPadding: valueLabelWidth + 10,
  };
  const height = heightForPlot(
    Math.max(120, categories.length * perRow),
    input
  );
  return {
    frame: cartesianFrame({ ...input, height }),
    labels: { rotated: false, lines, height: 0 },
  };
}

interface BarGeometry {
  frame: Frame;
  labels: CategoryLabelLayout;
  legend: LegendLayout | null;
  band: BandScale;
  valueScale: Scale;
  ticks: number[];
  tick: (value: number) => string;
}

function barGeometry(
  categories: BarCategory[],
  names: string[],
  orientation: BarOrientation,
  stacked: boolean,
  style: ChartStyle,
  fmt: NumberFormat
): BarGeometry {
  const rows = categories.map((category) => category.values);
  const [low, high] = barExtent(rows, stacked);
  const axis = niceTicks(low, high, { includeZero: true });
  const tick = tickFormat(fmt, axis.decimals);
  const legend =
    names.length > 1
      ? layoutLegend(names, CHART_WIDTH - 2 * CHART_PADDING)
      : null;
  const vertical = orientation === "vertical";
  const base: FrameBase = {
    width: CHART_WIDTH,
    title: style.apa ? "" : style.title,
    xTitle: vertical ? style.xTitle : style.yTitle,
    yTitle: vertical ? style.yTitle : style.xTitle,
    legendHeight: legend?.height ?? 0,
  };
  const valueLabelWidth = style.valueLabels
    ? maxTextWidth(
        rows.flat().map((v) => (v === null ? "" : valueText(fmt, v))),
        { fontSize: VALUE_SIZE }
      )
    : 0;
  const perRow = stacked || names.length === 1 ? 30 : names.length * 14 + 12;
  const { frame, labels } = vertical
    ? verticalFrame(base, categories, axis.ticks.map(tick), style.valueLabels)
    : horizontalFrame(base, categories, valueLabelWidth, perRow);
  const { plot } = frame;
  const along: [number, number] = vertical
    ? [plot.x, plot.x + plot.width]
    : [plot.y, plot.y + plot.height];
  const valueRange: [number, number] = vertical
    ? [plot.y + plot.height, plot.y]
    : [plot.x, plot.x + plot.width];
  return {
    frame,
    labels,
    legend,
    band: bandScale(categories.length, along, BAND_PADDING),
    valueScale: linearScale([axis.min, axis.max], valueRange),
    ticks: axis.ticks,
    tick,
  };
}

/** The total of a stacked bar's positive segments, printed past its end. */
function StackTotals({
  rows,
  band,
  valueScale,
  vertical,
  fmt,
}: {
  rows: (number | null)[][];
  band: BandScale;
  valueScale: Scale;
  vertical: boolean;
  fmt: NumberFormat;
}) {
  return (
    <g>
      {rows.map((values, row) => {
        const top = stackRow(values)
          .filter((segment) => segment.value > 0)
          .at(-1);
        if (!top) {
          return null;
        }
        const end = valueScale(top.end);
        const at = vertical
          ? { x: band.center(row), y: end - 4 }
          : { x: end + 4, y: band.center(row) + 4 };
        return (
          <text
            fill={CHART_INK.text}
            fontSize={VALUE_SIZE}
            fontWeight="bold"
            // biome-ignore lint/suspicious/noArrayIndexKey: one total per category, in order
            key={row}
            textAnchor={vertical ? "middle" : "start"}
            x={px(at.x)}
            y={px(at.y)}
          >
            {valueText(
              fmt,
              rowTotal(values.map((v) => (v !== null && v > 0 ? v : null)))
            )}
          </text>
        );
      })}
    </g>
  );
}

export function BarChart({
  id,
  categories,
  names,
  orientation,
  mode,
  style,
  fmt,
  label,
  description,
}: {
  /** The `<svg>` id, unique on the page. */
  id: string;
  categories: BarCategory[];
  /** Series names; more than one draws a legend. */
  names: string[];
  orientation: BarOrientation;
  mode: BarMode;
  style: ChartStyle;
  fmt: NumberFormat;
  label: string;
  description: string;
}) {
  const stacked = mode === "stacked" && names.length > 1;
  const vertical = orientation === "vertical";
  const rows = categories.map((category) => category.values);
  const { frame, labels, legend, band, valueScale, ticks, tick } = barGeometry(
    categories,
    names,
    orientation,
    stacked,
    style,
    fmt
  );
  const { plot } = frame;
  const bars = layoutBars({
    rows,
    mode: stacked ? "stacked" : "grouped",
    orientation,
    valueScale,
    band,
    maxThickness: 56,
  });
  const start = colorIndex(style.color);
  return (
    <ChartSvg
      description={description}
      height={frame.height}
      id={id}
      label={label}
      width={frame.width}
    >
      <ChartTitles frame={frame} />
      {legend ? (
        <SvgLegend
          keys={names.map((_, i) => ({
            color: paletteColor(start, i),
            kind: "swatch",
          }))}
          layout={legend}
          x={CHART_PADDING}
          y={frame.legendTop}
        />
      ) : null}
      <ValueAxis
        apa={style.apa}
        format={tick}
        orient={vertical ? "left" : "bottom"}
        plot={plot}
        scale={valueScale}
        ticks={ticks}
        zeroLine
      />
      <CategoryAxis
        apa={style.apa}
        labels={labels}
        orient={vertical ? "bottom" : "left"}
        plot={plot}
        positions={categories.map((_, i) => band.center(i))}
      />
      {bars.map((bar) => (
        <rect
          fill={paletteColor(start, bar.series)}
          height={px(bar.height)}
          key={`${bar.row}-${bar.series}`}
          stroke={stacked ? CHART_INK.background : "none"}
          strokeWidth={stacked ? 1 : 0}
          width={px(bar.width)}
          x={px(bar.x)}
          y={px(bar.y)}
        />
      ))}
      {style.valueLabels
        ? bars.map((bar) => (
            <BarValueLabel
              bar={bar}
              fill={paletteColor(start, bar.series)}
              inside={stacked}
              key={`v-${bar.row}-${bar.series}`}
              orientation={orientation}
              text={valueText(fmt, bar.value)}
            />
          ))
        : null}
      {style.valueLabels && stacked ? (
        <StackTotals
          band={band}
          fmt={fmt}
          rows={rows}
          valueScale={valueScale}
          vertical={vertical}
        />
      ) : null}
    </ChartSvg>
  );
}
